// src/utils/roleHelper.js

const ROLE_LABELS = {
  1: "Administrateur",
  2: "Utilisateur",
  3: "Éditeur",
  4: "Rédacteur",
  5: "Modérateur",
};

const normalizeModuleKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/_/g, "-");

const parseMaybeJson = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string") return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

/* =========================
   Helpers rôles classiques
   ========================= */

export function getRoleFromRoleId(roleId) {
  return ROLE_LABELS[Number(roleId)] || "Utilisateur";
}

export function checkRoleAccess(userRole, allowedRoles = []) {
  if (!userRole) return false;
  return allowedRoles.includes(userRole);
}

export function isAdmin(user) {
  return Number(user?.role_id) === 1;
}

/* =========================
   Helpers permissions admin
   ========================= */

export function getAdminPermissions(user) {
  if (!user) return [];

  const candidates = [
    user?.admin_permissions,
    user?.adminPermissions,
    user?.admin_modules,
    user?.adminModules,
    user?.permissions,
    user?.modules,
    user?.module_permissions,
    user?.modulePermissions,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;

    if (typeof candidate === "string") {
      const parsed = parseMaybeJson(candidate);
      if (parsed.length) return parsed;
    }
  }

  return [];
}

export function hasAdminModuleAccess(user, moduleKey, action = "view") {
  if (!user) return false;

  // Admin complet
  if (Number(user?.role_id) === 1) return true;

  const normalizedTarget = normalizeModuleKey(moduleKey);
  const permissions = getAdminPermissions(user);

  if (!permissions.length) return false;

  const modulePermission = permissions.find((item) => {
    const key =
      item?.module_key ||
      item?.moduleKey ||
      item?.module ||
      item?.key ||
      item?.name;

    return normalizeModuleKey(key) === normalizedTarget;
  });

  if (!modulePermission) return false;

  const canView =
    modulePermission?.can_view ??
    modulePermission?.canView ??
    modulePermission?.view ??
    false;

  const canEdit =
    modulePermission?.can_edit ??
    modulePermission?.canEdit ??
    modulePermission?.edit ??
    false;

  const canCreate =
    modulePermission?.can_create ??
    modulePermission?.canCreate ??
    modulePermission?.create ??
    canEdit;

  const canDelete =
    modulePermission?.can_delete ??
    modulePermission?.canDelete ??
    modulePermission?.delete ??
    canEdit;

  switch (action) {
    case "create":
      return !!canCreate;
    case "edit":
      return !!canEdit;
    case "delete":
      return !!canDelete;
    case "view":
    default:
      return !!canView;
  }
}

export function hasAnyAdminAccess(user) {
  if (!user) return false;
  if (Number(user?.role_id) === 1) return true;

  const permissions = getAdminPermissions(user);

  return permissions.some((item) => {
    const canView = item?.can_view ?? item?.canView ?? item?.view ?? false;
    return !!canView;
  });
}